import { EventType } from "@/analytics/analytics-events";
import { ShowPairwiseRegistrationReportEventProperties } from "@/analytics/pairwise-registration-events-properties";
import { OverlayToolbar } from "@/components/ui/overlay-toolbar";
import { useAlignmentOverlay } from "@/registration-tools/common/alignment-overlay-context";
import { useRegistrationContext } from "@/registration-tools/common/registration-context";
import { Perspective } from "@/registration-tools/common/store/registration-datatypes";
import { getMetricScaleIcon } from "@/registration-tools/utils/metrics";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  FaroSvgIcon,
  FaroTooltip,
  ResetIcon,
  ToolButton,
  ToolGroup,
  ViewPredefinedFrontIcon,
  ViewPredefinedRightIcon,
  ViewPredefinedTopIcon,
  ZoomAllIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Stack } from "@mui/material";
import { useCallback, type JSX } from "react";
import { ReactComponent as MetricsScaleUnknown } from "../icons/metrics-scale-unknown.svg";
import {
  selectCurrentPerspective,
  selectLastRegistrationPoseUsed,
} from "./store/registration-selectors";
import { setCurrentPerspective } from "./store/registration-slice";

type AlignmentCanvasOverlayProps = AlignmentToolBarProps;

/**
 * @param props - The properties for the alignment workflow overlay
 * @returns The overlay for the alignment workflow
 */
export function AlignmentCanvasOverlay(
  props: AlignmentCanvasOverlayProps,
): JSX.Element {
  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        p: 2,
      }}
    >
      <Stack direction="column" sx={{ justifyContent: "end" }}>
        <AlignmentViewToggle />
      </Stack>

      <Stack direction="column" sx={{ justifyContent: "center" }}>
        <AlignmentToolBar {...props} />
      </Stack>
    </Stack>
  );
}

/**
 * @returns A toggle button group to change the camera view between top view, front view and side view
 */
export function AlignmentViewToggle(): JSX.Element | null {
  const perspective = useAppSelector(selectCurrentPerspective);
  const dispatch = useAppDispatch();
  const { centerCameraOnPerspective } = useAlignmentOverlay();

  const onHandleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newPerspective?: Perspective) => {
      if (newPerspective) {
        Analytics.track(EventType.changePairwiseRegistrationView, {
          perspective: newPerspective,
        });
        dispatch(setCurrentPerspective(newPerspective));
        centerCameraOnPerspective.emit(newPerspective);
      }
    },
    [centerCameraOnPerspective, dispatch],
  );

  return (
    <OverlayToolbar>
      <ToolGroup
        value={perspective}
        orientation="vertical"
        exclusive
        onChange={onHandleChange}
      >
        <FaroTooltip title="Top view" placement="right">
          <ToolButton
            value={Perspective.topView}
            selected={perspective === Perspective.topView}
          >
            <ViewPredefinedTopIcon />
          </ToolButton>
        </FaroTooltip>
        <FaroTooltip title="Front view" placement="right">
          <ToolButton
            value={Perspective.frontView}
            selected={perspective === Perspective.frontView}
          >
            <ViewPredefinedFrontIcon />
          </ToolButton>
        </FaroTooltip>
        <FaroTooltip title="Side view" placement="right">
          <ToolButton
            value={Perspective.sideView}
            selected={perspective === Perspective.sideView}
          >
            <ViewPredefinedRightIcon />
          </ToolButton>
        </FaroTooltip>
      </ToolGroup>
    </OverlayToolbar>
  );
}

type AlignmentToolBarProps = {
  showResetTransformation: boolean;
};
/**
 * @returns A toolbar that allows to
 *           - reset the camera view
 *           - transform of the model point cloud
 *           - display registration metrics
 */
function AlignmentToolBar({
  showResetTransformation,
}: AlignmentToolBarProps): JSX.Element | null {
  const {
    modelCloudTransformReset,
    centerCameraOnPerspective,
    showRegistrationResultsDialog,
  } = useAlignmentOverlay();
  const perspective = useAppSelector(selectCurrentPerspective);
  const { registrationQuality } = useRegistrationContext();
  const metricScaleIcon = getMetricScaleIcon(registrationQuality);
  const lastRegistrationPoseUsed = useAppSelector(
    selectLastRegistrationPoseUsed,
  );

  return (
    <OverlayToolbar>
      {metricScaleIcon !== MetricsScaleUnknown && (
        <ToolGroup>
          <FaroTooltip
            title={
              lastRegistrationPoseUsed
                ? "Automatic Registration Quality"
                : "Registration Metrics are no longer valid because manual adjustments were made."
            }
            placement="left"
          >
            <ToolButton
              onClick={() => {
                Analytics.track<ShowPairwiseRegistrationReportEventProperties>(
                  EventType.showPairwiseRegistrationReport,
                  {
                    registrationQuality,
                  },
                );
                showRegistrationResultsDialog.emit();
              }}
              disabled={!lastRegistrationPoseUsed}
            >
              <FaroSvgIcon inheritViewBox source={metricScaleIcon} />
            </ToolButton>
          </FaroTooltip>
        </ToolGroup>
      )}
      {showResetTransformation && (
        <ToolGroup>
          <FaroTooltip title="Reset transformation" placement="left">
            <ToolButton
              onClick={() => {
                modelCloudTransformReset.emit();
              }}
            >
              <ResetIcon />
            </ToolButton>
          </FaroTooltip>
        </ToolGroup>
      )}
      <ToolGroup>
        <FaroTooltip title="Recenter view" placement="left">
          <ToolButton
            onClick={() => {
              Analytics.track(EventType.resetPairwiseRegistrationCamera);
              centerCameraOnPerspective.emit(perspective ?? null);
            }}
          >
            <ZoomAllIcon />
          </ToolButton>
        </FaroTooltip>
      </ToolGroup>
    </OverlayToolbar>
  );
}
